import { useState, useEffect } from 'react';
import * as api from '../../api/adminApi';
import { PageHeader, StatCard, StatRow, FilterPills, Pagination, StatusBadge, cardStyle, sectionTitleStyle, tableHeaderStyle, tableCellStyle, LoadingState, ErrorState, timeAgo, greenText } from './shared';

export default function ESignPage() {
  const [summary, setSummary] = useState<Awaited<ReturnType<typeof api.getEsignSummary>> | null>(null);
  const [requests, setRequests] = useState<Awaited<ReturnType<typeof api.getEsignRequests>> | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const [s, r] = await Promise.all([
        api.getEsignSummary(),
        api.getEsignRequests({ status: statusFilter === 'all' ? undefined : statusFilter, page }),
      ]);
      setSummary(s); setRequests(r);
    } catch (err: unknown) { setError((err as Error).message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, page]);

  if (loading && !summary) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div>
      <PageHeader title="E-Signatures" subtitle="Signing requests & completion tracking" />

      <StatRow>
        <StatCard label="TOTAL REQUESTS" value={String(summary?.total ?? 0)} sub="This month" />
        <StatCard label="COMPLETED" value={String(summary?.completed ?? 0)} valueColor="#22883F" accent={<span style={{ ...greenText, fontSize: 16 }}>{summary?.completionRate ?? 0}%</span>} sub="All parties signed" />
        <StatCard label="PENDING" value={String(summary?.pending ?? 0)} valueColor="#D46A3A" sub="Awaiting signatures" />
        <StatCard label="EXPIRED / DECLINED" value={String((summary?.expired ?? 0) + (summary?.revoked ?? 0))} valueColor="#D44A3A" sub={`${summary?.expired ?? 0} expired · ${summary?.revoked ?? 0} declined`} />
      </StatRow>

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={sectionTitleStyle}>Signing Requests</span>
          <FilterPills
            options={[
              { label: 'All', value: 'all' },
              { label: 'Pending', value: 'pending' },
              { label: 'Completed', value: 'completed' },
              { label: 'Expired', value: 'expired' },
            ]}
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
          />
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={tableHeaderStyle}>Document</th>
              <th style={tableHeaderStyle}>Sender</th>
              <th style={tableHeaderStyle}>Signers</th>
              <th style={tableHeaderStyle}>Status</th>
              <th style={tableHeaderStyle}>Fields</th>
              <th style={tableHeaderStyle}>Created</th>
            </tr>
          </thead>
          <tbody>
            {requests?.requests.map((r) => (
              <tr key={r.requestId}>
                <td style={tableCellStyle}>
                  <div style={{ fontWeight: 500 }}>{r.documentName}</div>
                  {r.pageCount && <div style={{ fontSize: 12, color: '#8B7D6B' }}>{r.pageCount} pages</div>}
                </td>
                <td style={tableCellStyle}>
                  {typeof r.senderId === 'object' ? r.senderId.displayName : '—'}
                </td>
                <td style={{ ...tableCellStyle, fontSize: 13, color: r.signerProgress.signed < r.signerProgress.total ? '#D46A3A' : '#22883F' }}>
                  {r.signerProgress.signed}/{r.signerProgress.total} signed
                </td>
                <td style={tableCellStyle}><StatusBadge status={r.status} /></td>
                <td style={{ ...tableCellStyle, color: '#8B7D6B' }}>{r.fieldCount ?? '—'}</td>
                <td style={{ ...tableCellStyle, color: '#8B7D6B' }}>{timeAgo(r.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {requests && <Pagination page={requests.page} totalPages={requests.totalPages} total={requests.total} label="requests" onPageChange={setPage} />}
      </div>
    </div>
  );
}
